// Compact license key row component

import React, { useState } from 'react';
import { Copy, Check, RefreshCw, Eye, EyeOff } from 'lucide-react';
import type { License } from '../types';

interface LicenseKeyRowProps {
  license: License;
  onRotate: () => void;
}

function maskKey(key: string): string {
  if (key.length <= 12) return key;
  return key.slice(0, 12) + '\u2022'.repeat(16);
}

export function LicenseKeyRow({ license, onRotate }: LicenseKeyRowProps) {
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(license.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const envLabel = license.environment === 'sandbox' ? 'SBX' : 'PRD';
  const envColor = license.environment === 'sandbox'
    ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
    : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';

  return (
    <div className="flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3 hover:border-accent/50 transition-colors">
      {/* Environment badge */}
      <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded border shrink-0 ${envColor}`}>
        {envLabel}
      </span>

      {/* License key (masked by default) */}
      <input
        type="text"
        readOnly
        value={revealed ? license.key : maskKey(license.key)}
        className="flex-1 min-w-0 bg-transparent text-sm font-mono text-foreground border-none outline-none cursor-default truncate"
        onClick={(e) => { if (revealed) (e.target as HTMLInputElement).select(); }}
      />

      {/* Reveal toggle */}
      <button
        onClick={() => setRevealed(!revealed)}
        className="text-muted-foreground hover:text-foreground transition-colors p-1.5 shrink-0"
        title={revealed ? 'Hide license key' : 'Show license key'}
      >
        {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>

      {/* Copy button */}
      <button
        onClick={copyToClipboard}
        className="text-muted-foreground hover:text-foreground transition-colors p-1.5 shrink-0"
        title="Copy license key"
      >
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
      </button>

      {/* Rotate button */}
      <button
        onClick={onRotate}
        className="text-muted-foreground hover:text-foreground transition-colors p-1.5 shrink-0"
        title="Rotate license key"
      >
        <RefreshCw className="w-4 h-4" />
      </button>
    </div>
  );
}
